/**
 * Amplitude Experiment feature flag keys (must match flags defined in Amplitude UI).
 * Add keys here as flags are created in Experiment; consumers use {@link useAmplitudeFeatureFlag}.
 */
export enum AMPLITUDE_FEATURE_FLAGS {
  /** Lease audits: Hot Spots sub-tab showing recurring failure patterns across properties */
  HOTSPOT = 'hotspot',
  /** Lease audits: recoverable revenue Impact column on audit tables; must match Amplitude Experiment key */
  LEASE_AUDIT_FINANCIAL_IMPACT_UI = 'lease-audit-financial-impact-ui',
  /** Property Transitions customer UI rollout; must match Amplitude Experiment key */
  PROPERTY_TRANSITIONS_UI = 'property-transitions-ui',
  /** Lease audit explicit rerun UI (#8879); must match Amplitude Experiment key */
  LEASE_AUDIT_EXPLICIT_RERUN = 'lease-audit-explicit-rerun',
  /**
   * Lease audits: Check Type column in place of Failure Type, the AI Issue Status column,
   * the Needs Customer Input request-type filter, and the link from a blocked issue to its
   * comments (#10349); must match Amplitude Experiment key
   */
  LEASE_AUDIT_CHECK_TYPE_AND_ISSUE_STATUS_UI = 'lease-audit-check-type-and-issue-status-ui',
  /** Property Health: regroup an expanded financial report by property or occupancy type; must match Amplitude Experiment key */
  LEASE_AUDIT_REPORT_GROUPING = 'lease-audit-report-grouping',
  /** Property transitions Key Data / variable catalog; must match Amplitude Experiment key */
  TRANSITION_VARIABLES = 'transition-variables',
  /** What's New dialog, archive page, and account-menu link; must match Amplitude Experiment key */
  WHATS_NEW = 'whats-new',
  /** Document management customer UI; must match Amplitude Experiment key */
  DOCUMENT_MANAGEMENT = 'document-management',
  /**
   * Lease audit run documents: reconciled-documents API and document timeline;
   * must match Amplitude Experiment key
   */
  USE_RECONCILED_DOCUMENTS_API = 'use-reconciled-documents-api',
  /**
   * Lease audits: consolidated expandable table on Home and All audits
   * (resident-first lease cell, expand-left, shared columns); must match
   * Amplitude Experiment key
   */
  LEASE_AUDIT_CONSOLIDATED_TABLE = 'lease-audit-consolidated-table',
  /**
   * Surface Agent panel, topbar toggle, sidebar entry, and fullscreen page
   * for property transitions; must match Amplitude Experiment key
   */
  SURFACE_AGENT_PROPERTY_TRANSITIONS = 'surface-agent-property-transitions',
}

/**
 * Calendar day each flag was added to the app (`YYYY-MM-DD`).
 * The debug drawer sorts by this date, newest first.
 * A missing key is a type error.
 */
export const AMPLITUDE_FEATURE_FLAG_ADDED_ON = {
  [AMPLITUDE_FEATURE_FLAGS.HOTSPOT]: '2026-05-28',
  [AMPLITUDE_FEATURE_FLAGS.LEASE_AUDIT_FINANCIAL_IMPACT_UI]: '2026-06-15',
  [AMPLITUDE_FEATURE_FLAGS.PROPERTY_TRANSITIONS_UI]: '2026-06-16',
  [AMPLITUDE_FEATURE_FLAGS.LEASE_AUDIT_EXPLICIT_RERUN]: '2026-06-24',
  [AMPLITUDE_FEATURE_FLAGS.LEASE_AUDIT_CHECK_TYPE_AND_ISSUE_STATUS_UI]:
    '2026-09-01',
  [AMPLITUDE_FEATURE_FLAGS.LEASE_AUDIT_REPORT_GROUPING]: '2026-09-02',
  [AMPLITUDE_FEATURE_FLAGS.TRANSITION_VARIABLES]: '2026-09-09',
  [AMPLITUDE_FEATURE_FLAGS.WHATS_NEW]: '2026-09-14',
  [AMPLITUDE_FEATURE_FLAGS.DOCUMENT_MANAGEMENT]: '2026-09-17',
  [AMPLITUDE_FEATURE_FLAGS.USE_RECONCILED_DOCUMENTS_API]: '2026-09-17',
  [AMPLITUDE_FEATURE_FLAGS.LEASE_AUDIT_CONSOLIDATED_TABLE]: '2026-09-23',
  [AMPLITUDE_FEATURE_FLAGS.SURFACE_AGENT_PROPERTY_TRANSITIONS]: '2026-10-06',
} as const satisfies Record<AMPLITUDE_FEATURE_FLAGS, string>;

/** Flags at least this many calendar days old are called out in the debug drawer. */
export const STALE_AMPLITUDE_FEATURE_FLAG_AGE_DAYS = 90;

const MS_PER_DAY = 86_400_000;

function addedOnUtcMs(addedOn: string): number {
  const [year, month, day] = addedOn.split('-').map(Number);
  return Date.UTC(year, month - 1, day);
}

/** Calendar days from the flag's added-on date through `now`'s local calendar day. */
export function amplitudeFeatureFlagAgeDays(
  flag: AMPLITUDE_FEATURE_FLAGS,
  now: Date = new Date()
): number {
  const added = addedOnUtcMs(AMPLITUDE_FEATURE_FLAG_ADDED_ON[flag]);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today - added) / MS_PER_DAY);
}

export function isAmplitudeFeatureFlagStale(
  flag: AMPLITUDE_FEATURE_FLAGS,
  now: Date = new Date()
): boolean {
  return (
    amplitudeFeatureFlagAgeDays(flag, now) >=
    STALE_AMPLITUDE_FEATURE_FLAG_AGE_DAYS
  );
}

/** Newest added-on date first. Equal dates sort by flag key. */
export function compareAmplitudeFeatureFlagsByAge(
  a: AMPLITUDE_FEATURE_FLAGS,
  b: AMPLITUDE_FEATURE_FLAGS
): number {
  const addedA = AMPLITUDE_FEATURE_FLAG_ADDED_ON[a];
  const addedB = AMPLITUDE_FEATURE_FLAG_ADDED_ON[b];
  if (addedA !== addedB) {
    return addedA < addedB ? 1 : -1;
  }
  return a < b ? -1 : a > b ? 1 : 0;
}

/** `M/D/YYYY` from the stored `YYYY-MM-DD`, without `Date` parsing. */
export function formatAmplitudeFeatureFlagAddedOn(
  flag: AMPLITUDE_FEATURE_FLAGS
): string {
  const [year, month, day] = AMPLITUDE_FEATURE_FLAG_ADDED_ON[flag].split('-');
  return `${Number(month)}/${Number(day)}/${year}`;
}
